/**
 * Beloved — Daily masterpiece data module
 *
 * A curated set of public-domain, broad-Christian artworks (Christ-centred and
 * biblical scenes, plus creation) bundled locally in `public/art/` so the app
 * stays offline-safe. There is intentionally NO Marian devotion and NO saint
 * portraiture — the subjects are the life of Christ, biblical moments, and the
 * glory of creation.
 *
 * The rotation is day-keyed (day-of-year modulo the number of works), mirroring
 * the daily verse in `verses.js`: everyone sees the same painting on the same
 * day, and it changes each day.
 *
 * Shape: { file, fallback, name, artist, year, scripture, reference }
 *  - `file`      public path to the WebP image used by supported app targets
 *  - `fallback`  matching JPEG retained for compatibility and future `<picture>` use
 *  - `name`      the work's title
 *  - `artist`    the painter
 *  - `year`      completion year (string, for the caption e.g. "Rembrandt, 1669")
 *  - `scripture` a short caption line in World English Bible (WEB) wording
 *  - `reference` the scripture reference (e.g. 'Luke 15:20')
 */

/**
 * The curated artworks. Every file listed here exists in `public/art/`.
 * @type {ReadonlyArray<{file: string, fallback: string, name: string, artist: string, year: string, scripture: string, reference: string}>}
 */
export const ARTWORKS = [
  // ---- Already bundled (migrated from the former SACRED_IMAGES) ----
  {
    file: '/art/baptism.webp',
    fallback: '/art/baptism.jpg',
    name: 'The Baptism of Christ',
    artist: 'Verrocchio & Leonardo',
    year: '1475',
    scripture: 'This is my beloved Son, in whom I am well pleased.',
    reference: 'Matthew 3:17',
  },
  {
    file: '/art/prodigal.webp',
    fallback: '/art/prodigal.jpg',
    name: 'The Return of the Prodigal Son',
    artist: 'Rembrandt',
    year: '1669',
    scripture: 'While he was still far off, his father saw him, and was moved with compassion.',
    reference: 'Luke 15:20',
  },
  {
    file: '/art/blessing.webp',
    fallback: '/art/blessing.jpg',
    name: 'Christ Giving His Blessing',
    artist: 'Hans Memling',
    year: '1478',
    scripture: 'Peace I leave with you. My peace I give to you.',
    reference: 'John 14:27',
  },
  {
    file: '/art/emmaus.webp',
    fallback: '/art/emmaus.jpg',
    name: 'The Supper at Emmaus',
    artist: 'Caravaggio',
    year: '1601',
    scripture: 'Their eyes were opened, and they recognised him.',
    reference: 'Luke 24:31',
  },
  {
    file: '/art/resurrection.webp',
    fallback: '/art/resurrection.jpg',
    name: 'The Resurrection',
    artist: 'Piero della Francesca',
    year: '1465',
    scripture: 'He is not here, for he has risen, just like he said.',
    reference: 'Matthew 28:6',
  },

  // ---- Added for the daily rotation ----
  {
    file: '/art/sermon.webp',
    fallback: '/art/sermon.jpg',
    name: 'The Sermon on the Mount',
    artist: 'Carl Bloch',
    year: '1877',
    scripture: 'Blessed are the poor in spirit, for theirs is the Kingdom of Heaven.',
    reference: 'Matthew 5:3',
  },
  {
    file: '/art/storm.webp',
    fallback: '/art/storm.jpg',
    name: 'Christ in the Storm on the Sea of Galilee',
    artist: 'Rembrandt',
    year: '1633',
    scripture: 'He arose, and rebuked the wind, and said to the sea, “Peace! Be still!”',
    reference: 'Mark 4:39',
  },
  {
    file: '/art/starry.webp',
    fallback: '/art/starry.jpg',
    name: 'The Starry Night',
    artist: 'Vincent van Gogh',
    year: '1889',
    scripture: 'The heavens declare the glory of God. The expanse shows his handiwork.',
    reference: 'Psalm 19:1',
  },
  {
    file: '/art/gethsemane.webp',
    fallback: '/art/gethsemane.jpg',
    name: 'Christ in Gethsemane',
    artist: 'Heinrich Hofmann',
    year: '1890',
    scripture: 'Not my will, but yours, be done.',
    reference: 'Luke 22:42',
  },
  {
    file: '/art/shepherd.webp',
    fallback: '/art/shepherd.jpg',
    name: 'The Good Shepherd',
    artist: 'Bernhard Plockhorst',
    year: '1878',
    scripture: 'I am the good shepherd. The good shepherd lays down his life for the sheep.',
    reference: 'John 10:11',
  },
  {
    file: '/art/lazarus.webp',
    fallback: '/art/lazarus.jpg',
    name: 'The Raising of Lazarus',
    artist: 'Rembrandt',
    year: '1631',
    scripture: 'He cried with a loud voice, “Lazarus, come out!”',
    reference: 'John 11:43',
  },
  {
    file: '/art/matthew.webp',
    fallback: '/art/matthew.jpg',
    name: 'The Calling of Saint Matthew',
    artist: 'Caravaggio',
    year: '1600',
    scripture: 'He said to him, “Follow me.” He got up and followed him.',
    reference: 'Matthew 9:9',
  },
  {
    file: '/art/richruler.webp',
    fallback: '/art/richruler.jpg',
    name: 'Christ and the Rich Young Ruler',
    artist: 'Heinrich Hofmann',
    year: '1889',
    scripture: 'Come, follow me. And treasure will be yours in heaven.',
    reference: 'Mark 10:21',
  },
  {
    file: '/art/lightoftheworld.webp',
    fallback: '/art/lightoftheworld.jpg',
    name: 'The Light of the World',
    artist: 'William Holman Hunt',
    year: '1854',
    scripture: 'Behold, I stand at the door and knock. If anyone hears my voice and opens the door, I will come in.',
    reference: 'Revelation 3:20',
  },
  {
    file: '/art/children.webp',
    fallback: '/art/children.jpg',
    name: 'Let the Little Children Come to Me',
    artist: 'Fritz von Uhde',
    year: '1884',
    scripture: 'Allow the little children to come to me. Don’t forbid them.',
    reference: 'Mark 10:14',
  },
  {
    file: '/art/healing.webp',
    fallback: '/art/healing.jpg',
    name: 'Christ Healing the Sick',
    artist: 'Carl Bloch',
    year: '1883',
    scripture: 'He went about doing good, and healing all who were oppressed.',
    reference: 'Acts 10:38',
  },
];

/**
 * The day of the year for a given date, 1 (1 January) through 366 (29 December
 * in a leap year). Computed in local time to match the daily verse.
 * @param {Date} date the date to evaluate
 * @returns {number} the day of the year, 1-based
 */
function dayOfYear(date) {
  const startOfYear = new Date(date.getFullYear(), 0, 0);
  const diff = date - startOfYear;
  return Math.floor(diff / 86400000);
}

/**
 * The artwork for a given date (defaults to today), chosen by a deterministic
 * day-keyed rotation so everyone sees the same painting on the same day and it
 * changes daily.
 * @param {Date} [date] the date to look up
 * @returns {{file: string, fallback: string, name: string, artist: string, year: string, scripture: string, reference: string}} the day's artwork
 */
export function getArtworkOfDay(date = new Date()) {
  const index = (dayOfYear(date) - 1) % ARTWORKS.length;
  return ARTWORKS[index];
}

export default ARTWORKS;
